// ============================================================================
// AI ACTIVITY PULSE OVERLAY - Bottom-right HUD showing when AI is processing
// Slides in when avatars are thinking or a task is running, lingers 2s after
// ============================================================================

import React, { useEffect, useState, useRef } from 'react';
import { useSecondScreenData } from '../../contexts/SecondScreenContext';

const LINGER_MS = 2_000;

const AiActivityPulseOverlay: React.FC = () => {
  const { state } = useSecondScreenData();
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const lingerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const runningTask = state?.activeTasks?.find(t => (t as any).status === 'running');
  const isActive = (state?.isAvatarsThinking ?? false) || !!runningTask;

  let label: string;
  if (state?.isAvatarsThinking) label = 'Avatars synthesising responses...';
  else if (runningTask) label = `Running: ${(runningTask as any).name || 'AI task'}...`;
  else label = 'AI processing...';

  useEffect(() => {
    if (isActive) {
      if (lingerTimerRef.current) clearTimeout(lingerTimerRef.current);
      setIsExiting(false);
      setIsVisible(true);
    } else if (isVisible) {
      lingerTimerRef.current = setTimeout(() => {
        setIsExiting(true);
        // Remove from DOM after exit animation completes
        setTimeout(() => {
          setIsVisible(false);
          setIsExiting(false);
        }, 350);
      }, LINGER_MS);
    }
    return () => {
      if (lingerTimerRef.current) clearTimeout(lingerTimerRef.current);
    };
  }, [isActive]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isVisible) return null;

  return (
    <div
      className={`fixed bottom-8 right-8 z-[85] flex items-center gap-3 px-4 py-3 rounded-2xl bg-black/75 backdrop-blur-md border border-white/10 ${
        isExiting ? 'animate-slide-out-right' : 'animate-slide-in-right'
      }`}
    >
      {/* Glowing orb */}
      <div className="w-3 h-3 rounded-full bg-primary animate-orb-pulse shrink-0" />

      {/* Status text */}
      <span className="text-sm text-white/70 font-medium whitespace-nowrap">{label}</span>
    </div>
  );
};

export default AiActivityPulseOverlay;
